import type { VCardContact, VCardProfile } from './api/types';

/** The fields Entra supplies, which a sync may replace. Everything else on a card is only ever typed in. */
export const ENTRA_FIELDS: ReadonlyArray<keyof VCardContact & keyof VCardProfile> = [
  'firstName',
  'lastName',
  'organization',
  'title',
  'email',
  'mobile',
  'workPhone',
  'street',
  'city',
  'stateRegion',
  'postcode',
  'country',
];

/** What a Kersia card says when Entra leaves it out. */
export const COMPANY_DEFAULTS = {
  organization: 'Kersia',
  website: 'https://www.kersia-group.com',
} as const;

function same(a: string | null | undefined, b: string | null | undefined): boolean {
  return (a ?? '').trim() === (b ?? '').trim();
}

/** A fresh card from a profile, with the company defaults where Entra has nothing. */
export function contactFromProfile(profile: VCardProfile): VCardContact {
  return {
    firstName: profile.firstName ?? '',
    lastName: profile.lastName ?? '',
    organization: profile.organization || COMPANY_DEFAULTS.organization,
    title: profile.title ?? '',
    email: profile.email ?? '',
    mobile: profile.mobile ?? '',
    workPhone: profile.workPhone ?? '',
    street: profile.street ?? '',
    city: profile.city ?? '',
    stateRegion: profile.stateRegion ?? '',
    postcode: profile.postcode ?? '',
    country: profile.country ?? '',
    website: COMPANY_DEFAULTS.website,
    linkedin: '',
    note: '',
    hiddenFields: [],
    includePhoto: true,
  };
}

/**
 * Only what the directory supplied replaces the card: a profile it could read only in part must not blank the rest,
 * and what Entra does not hold - website, LinkedIn, note, the hidden fields, the photo choice - is never touched.
 */
export function withProfile(contact: VCardContact, profile: VCardProfile): VCardContact {
  const synced = { ...contact };
  for (const key of ENTRA_FIELDS) {
    const value = profile[key];
    if (value) {
      synced[key] = value;
    }
  }

  return synced;
}

/** The Entra values the card differs from, by field: what a sync would change. */
export function entraDifferences(
  contact: VCardContact,
  profile: VCardProfile | null,
): Partial<Record<keyof VCardContact, string>> {
  const differences: Partial<Record<keyof VCardContact, string>> = {};
  if (!profile) {
    return differences;
  }

  for (const key of ENTRA_FIELDS) {
    const value = profile[key];
    if (value && !same(contact[key] as string | undefined, value)) {
      differences[key] = value;
    }
  }

  return differences;
}

/** The card as saved from the API, its nulls turned into the empty strings the form works with. */
export function contactForForm(contact: VCardContact): VCardContact {
  const cleaned = Object.fromEntries(Object.entries(contact).map(([key, value]) => [key, value ?? ''])) as VCardContact;
  return { ...cleaned, hiddenFields: contact.hiddenFields ?? [], includePhoto: contact.includePhoto ?? true };
}

/** Whether two cards hand out the same thing: field values trimmed, the hidden list in any order, the photo choice. */
export function sameCard(a: VCardContact, b: VCardContact): boolean {
  const text = (contact: VCardContact) =>
    Object.entries(contact)
      .filter(([key]) => key !== 'hiddenFields' && key !== 'includePhoto')
      .map(([key, value]) => [key, typeof value === 'string' ? value.trim() : (value ?? '')] as const)
      .filter(([, value]) => value !== '')
      .sort(([left], [right]) => left.localeCompare(right));
  const hidden = (contact: VCardContact) => [...(contact.hiddenFields ?? [])].sort();

  return (
    JSON.stringify(text(a)) === JSON.stringify(text(b)) &&
    JSON.stringify(hidden(a)) === JSON.stringify(hidden(b)) &&
    (a.includePhoto ?? true) === (b.includePhoto ?? true)
  );
}
